import React, {
    useState,
    useEffect,
    useMemo,
    useCallback
} from "react";

import Swal from "sweetalert2";

import {
    Table,
    Button,
    Input,
    Select,
    Modal,
    Form,
    AutoComplete,
    InputNumber,
    Card,
    Row,
    Col,
    Statistic,
    Tag,
    Space,
    Tooltip,
    Popconfirm,
    Alert,
    Spin,
    Empty,
    Divider,
    Typography
} from "antd";

import {
    SearchOutlined,
    PlusOutlined,
    DownloadOutlined,
    EditOutlined,
    DeleteOutlined,
    DollarOutlined,
    WalletOutlined,
    ReloadOutlined,
    FilterOutlined,
    LockOutlined,
    CheckCircleOutlined,
    WarningOutlined
} from "@ant-design/icons";

import {
    useAcademicYear
} from "../AcademicYearContext";

import api from "../api/api";

import {
    generatePaymentPDF
} from "./FinancialPaymentRecordPDF";

const {
    Title,
    Text
} = Typography;

const {
    Option
} = Select;

const FinancialPaymentRecord = () => {
    const token = localStorage.getItem("access");

    const {
        selectedYear
    } = useAcademicYear();

    const [form] = Form.useForm();

    const [payments, setPayments] = useState([]);
    const [students, setStudents] = useState([]);
    const [grades, setGrades] = useState([]);
    const [schoolFeesData, setSchoolFeesData] = useState([]);

    const [
        selectedStudentPayments,
        setSelectedStudentPayments
    ] = useState([]);

    const [totalPayments, setTotalPayments] = useState(0);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [loading, setLoading] = useState(false);

    const [
        supportingDataLoading,
        setSupportingDataLoading
    ] = useState(false);

    const [
        studentPaymentLoading,
        setStudentPaymentLoading
    ] = useState(false);

    const [search, setSearch] = useState("");
    const [selectedGrade, setSelectedGrade] = useState("");

    const [openDialog, setOpenDialog] = useState(false);
    const [editId, setEditId] = useState(null);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [
        maximumInstallmentAmount,
        setMaximumInstallmentAmount
    ] = useState(0);

    const [
        originalInstallmentAmount,
        setOriginalInstallmentAmount
    ] = useState(0);

    const [
        previouslyPaidAmount,
        setPreviouslyPaidAmount
    ] = useState(0);

    const [
        formData,
        setFormData
    ] = useState({
        semester: "",
        installment: "1st Installment",
        student: "",
        student_class: "",
        student_type: "",
        total_fee: "",
        payer_name: "",
        amount_paid: "",
        payment_currency: "LRD",
        exchange_rate: "",
        date: ""
    });

    const [
        financialSummary,
        setFinancialSummary
    ] = useState({
        total_entries: 0,
        total_fee: 0,
        total_paid: 0,
        total_paid_lrd: 0,
        total_balance: 0
    });

    const formatMoney = useCallback(
        value => {
            return Number(
                value || 0
            ).toLocaleString(
                undefined,
                {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }
            );
        },
        []
    );

    const normalizeStudentType = useCallback(
        value => {
            if (!value) {
                return "";
            }

            const normalized =
                String(value)
                    .trim()
                    .toLowerCase();

            if (
                normalized === "old" ||
                normalized === "old student"
            ) {
                return "old";
            }

            if (
                normalized === "new" ||
                normalized === "new student"
            ) {
                return "new";
            }

            if (
                normalized === "scholarship" ||
                normalized === "scholarship student"
            ) {
                return "scholarship";
            }

            if (
                normalized === "ward" ||
                normalized === "ward student"
            ) {
                return "ward";
            }

            return normalized;
        },
        []
    );

    const getSelectedStudent = useCallback(
        () => {
            return students.find(
                student =>
                    Number(student.id) ===
                    Number(formData.student)
            );
        },
        [
            students,
            formData.student
        ]
    );

    const getPaymentCurrency = useCallback(
        payment => {
            return (
                payment?.payment_currency ||
                "LRD"
            ).toUpperCase();
        },
        []
    );

    const getPaymentAmountLrd = useCallback(
        payment => {
            const savedLrd = Number(
                payment?.amount_paid_lrd || 0
            );

            if (
                Number.isFinite(savedLrd) &&
                savedLrd > 0
            ) {
                return savedLrd;
            }

            return Number(
                payment?.amount_paid || 0
            );
        },
        []
    );

    const convertToLrd = useCallback(
        (
            amount,
            currency,
            exchangeRate
        ) => {
            const numericAmount =
                Number(amount || 0);

            if (!Number.isFinite(numericAmount)) {
                return 0;
            }

            if (
                String(currency || "LRD")
                    .toUpperCase() === "LRD"
            ) {
                return Number(
                    numericAmount.toFixed(2)
                );
            }

            const rate =
                Number(
                    exchangeRate || 0
                );

            if (
                !Number.isFinite(rate) ||
                rate <= 0
            ) {
                return 0;
            }

            return Number(
                (
                    numericAmount *
                    rate
                ).toFixed(2)
            );
        },
        []
    );

    const getMatchedFeeRecord = useCallback(
        (
            studentClass,
            studentType
        ) => {
            if (
                !studentClass ||
                !studentType
            ) {
                return null;
            }

            const normalizedType =
                normalizeStudentType(
                    studentType
                );

            return schoolFeesData.find(
                fee => {
                    const feeType =
                        normalizeStudentType(
                            fee.student_type
                        );

                    return (
                        Number(
                            fee.grade_class
                        ) ===
                        Number(
                            studentClass
                        ) &&
                        feeType ===
                        normalizedType
                    );
                }
            ) || null;
        },
        [
            schoolFeesData,
            normalizeStudentType
        ]
    );

    const getInstallmentAmounts = useCallback(
        feeRecord => {
            if (!feeRecord) {
                return {};
            }

            return {
                Registration:
                    Number(
                        feeRecord.registration || 0
                    ),
                "1st Installment":
                    Number(
                        feeRecord.first_installment || 0
                    ),
                "2nd Installment":
                    Number(
                        feeRecord.second_installment || 0
                    ),
                "3rd Installment":
                    Number(
                        feeRecord.third_installment || 0
                    ),
                "4th Installment":
                    Number(
                        feeRecord.fourth_installment || 0
                    )
            };
        },
        []
    );

    const getPaymentInstallment = useCallback(
        payment => {
            return (
                payment?.installment ||
                ""
            ).trim();
        },
        []
    );

    const getAlreadyPaidForInstallment = useCallback(
        (
            installment,
            paymentsList = selectedStudentPayments,
            semester = null,
            excludePaymentId = null
        ) => {
            if (!installment) {
                return 0;
            }

            return paymentsList
                .filter(
                    payment => {
                        if (
                            getPaymentInstallment(
                                payment
                            ) !== installment
                        ) {
                            return false;
                        }

                        if (
                            semester &&
                            payment.semester !== semester
                        ) {
                            return false;
                        }

                        if (
                            excludePaymentId &&
                            Number(payment.id) ===
                            Number(excludePaymentId)
                        ) {
                            return false;
                        }

                        return true;
                    }
                )
                .reduce(
                    (
                        total,
                        payment
                    ) =>
                        total +
                        getPaymentAmountLrd(
                            payment
                        ),
                    0
                );
        },
        [
            selectedStudentPayments,
            getPaymentInstallment,
            getPaymentAmountLrd
        ]
    );

    const calculateInstallmentMaximum = useCallback(
        (
            studentId,
            studentClass,
            studentType,
            installment,
            paymentHistory = selectedStudentPayments,
            semester = null,
            excludePaymentId = null
        ) => {
            if (
                !studentId ||
                !studentClass ||
                !studentType ||
                !installment
            ) {
                return 0;
            }

            const feeRecord =
                getMatchedFeeRecord(
                    studentClass,
                    studentType
                );

            if (!feeRecord) {
                return 0;
            }

            const amounts =
                getInstallmentAmounts(
                    feeRecord
                );

            let originalAmount =
                Number(
                    amounts[installment] || 0
                );

            const student =
                students.find(
                    item =>
                        Number(item.id) ===
                        Number(studentId)
                );

            if (
                student &&
                [
                    "Dependent",
                    "Scholarship",
                    "dependent",
                    "scholarship",
                    "ward"
                ].includes(
                    student.student_status
                ) &&
                installment !==
                    "Registration"
            ) {
                originalAmount =
                    originalAmount * 0.50;
            }

            const alreadyPaid =
                getAlreadyPaidForInstallment(
                    installment,
                    paymentHistory,
                    semester,
                    excludePaymentId
                );

            const remaining =
                Math.max(
                    originalAmount -
                    alreadyPaid,
                    0
                );

            return Number(
                remaining.toFixed(2)
            );
        },
        [
            students,
            selectedStudentPayments,
            getMatchedFeeRecord,
            getInstallmentAmounts,
            getAlreadyPaidForInstallment
        ]
    );

    const getInstallmentInformation = useCallback(
        (
            studentId,
            studentClass,
            studentType,
            installment,
            paymentHistory,
            semester = null,
            excludePaymentId = null
        ) => {
            const feeRecord =
                getMatchedFeeRecord(
                    studentClass,
                    studentType
                );

            if (!feeRecord) {
                return {
                    originalAmount: 0,
                    previouslyPaid: 0,
                    remainingBalance: 0
                };
            }

            const amounts =
                getInstallmentAmounts(
                    feeRecord
                );

            let originalAmount =
                Number(
                    amounts[installment] || 0
                );

            const student =
                students.find(
                    item =>
                        Number(item.id) ===
                        Number(studentId)
                );

            if (
                student &&
                [
                    "Dependent",
                    "Scholarship",
                    "dependent",
                    "scholarship",
                    "ward"
                ].includes(
                    student.student_status
                ) &&
                installment !==
                    "Registration"
            ) {
                originalAmount =
                    originalAmount * 0.50;
            }

            const alreadyPaid =
                getAlreadyPaidForInstallment(
                    installment,
                    paymentHistory,
                    semester,
                    excludePaymentId
                );

            const remainingBalance =
                Math.max(
                    originalAmount -
                    alreadyPaid,
                    0
                );

            return {
                originalAmount:
                    Number(
                        originalAmount.toFixed(2)
                    ),
                previouslyPaid:
                    Number(
                        alreadyPaid.toFixed(2)
                    ),
                remainingBalance:
                    Number(
                        remainingBalance.toFixed(2)
                    )
            };
        },
        [
            students,
            getMatchedFeeRecord,
            getInstallmentAmounts,
            getAlreadyPaidForInstallment
        ]
    );

    const fetchStudentPaymentHistory =
        useCallback(
            async studentId => {
                if (
                    !studentId ||
                    !selectedYear ||
                    !token
                ) {
                    setSelectedStudentPayments([]);
                    return [];
                }

                try {
                    setStudentPaymentLoading(
                        true
                    );

                    const response =
                        await api.get(
                            "/api/users/payments/",
                            {
                                headers: {
                                    Authorization:
                                        `Bearer ${token}`
                                },
                                params: {
                                    academic_year:
                                        selectedYear.id,
                                    student_id:
                                        studentId,
                                    page_size:
                                        1000
                                }
                            }
                        );

                    const data =
                        response.data;

                    let history =
                        data?.results ||
                        data ||
                        [];

                    if (
                        !Array.isArray(history)
                    ) {
                        history = [];
                    }

                    history =
                        history.filter(
                            payment =>
                                Number(
                                    payment.student
                                ) ===
                                Number(
                                    studentId
                                )
                        );

                    setSelectedStudentPayments(
                        history
                    );

                    return history;
                } catch (err) {
                    console.error(
                        "Failed to fetch student payment history:",
                        err
                    );

                    const fallback =
                        payments.filter(
                            payment =>
                                Number(
                                    payment.student
                                ) ===
                                Number(studentId)
                        );

                    setSelectedStudentPayments(
                        fallback
                    );

                    return fallback;
                } finally {
                    setStudentPaymentLoading(
                        false
                    );
                }
            },
            [
                token,
                selectedYear,
                payments
            ]
        );

    const fetchPayments = useCallback(
        async () => {
            if (
                !token ||
                !selectedYear
            ) {
                return;
            }

            try {
                setLoading(true);

                const params = {
                    page,
                    page_size:
                        rowsPerPage,
                    academic_year:
                        selectedYear.id,
                    exclude_blacklisted:
                        true
                };

                if (search) {
                    params.search =
                        search;
                }

                if (selectedGrade) {
                    params.student_class =
                        selectedGrade;
                }

                const response =
                    await api.get(
                        "/api/users/payments/",
                        {
                            headers: {
                                Authorization:
                                    `Bearer ${token}`
                            },
                            params
                        }
                    );

                setPayments(
                    response.data.results ||
                    response.data ||
                    []
                );

                setTotalPayments(
                    response.data.count ||
                    0
                );

                setFinancialSummary(
                    response.data.summary ||
                    {
                        total_entries: 0,
                        total_fee: 0,
                        total_paid: 0,
                        total_paid_lrd: 0,
                        total_balance: 0
                    }
                );
            } catch (err) {
                console.error(err);

                setError(
                    "Failed to load payment records."
                );
            } finally {
                setLoading(false);
            }
        },
        [
            token,
            selectedYear,
            page,
            rowsPerPage,
            search,
            selectedGrade
        ]
    );

    const fetchSupportingData =
        useCallback(
            async () => {
                if (
                    !token ||
                    !selectedYear
                ) {
                    return;
                }

                try {
                    setSupportingDataLoading(
                        true
                    );

                    const config = {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    };

                    const [
                        studentsResponse,
                        gradesResponse,
                        feesResponse
                    ] = await Promise.all([
                        api.get(
                            "/api/users/students/",
                            {
                                ...config,
                                params: {
                                    academic_year:
                                        selectedYear.id
                                }
                            }
                        ),
                        api.get(
                            "/api/users/grade-classes/",
                            config
                        ),
                        api.get(
                            "/api/users/school-fees-data/",
                            {
                                ...config,
                                params: {
                                    academic_year:
                                        selectedYear.id
                                }
                            }
                        )
                    ]);

                    setStudents(
                        studentsResponse.data.results ||
                        studentsResponse.data ||
                        []
                    );

                    setGrades(
                        gradesResponse.data.results ||
                        gradesResponse.data ||
                        []
                    );

                    setSchoolFeesData(
                        feesResponse.data.results ||
                        feesResponse.data ||
                        []
                    );
                } catch (err) {
                    console.error(err);

                    setError(
                        "Failed to load supporting data."
                    );
                } finally {
                    setSupportingDataLoading(
                        false
                    );
                }
            },
            [
                token,
                selectedYear
            ]
        );

    useEffect(
        () => {
            if (selectedYear) {
                fetchSupportingData();
            }
        },
        [
            selectedYear,
            fetchSupportingData
        ]
    );

    useEffect(
        () => {
            if (selectedYear) {
                fetchPayments();
            }
        },
        [
            selectedYear,
            fetchPayments
        ]
    );

    const studentOptions = useMemo(
        () => {
            return students.map(
                student => {
                    const admissionNumber =
                        student.admission_id ||
                        "Student";

                    const gradeClass =
                        student.grade_class_name ||
                        student.grade_class?.name ||
                        student.grade_class ||
                        "No Class Assigned";

                    return {
                        value:
                            student.full_name,
                        label: (
                            <div
                                style={{
                                    display:
                                        "flex",
                                    flexDirection:
                                        "column",
                                    lineHeight:
                                        1.35,
                                    padding:
                                        "3px 0",
                                }}
                            >
                                <strong
                                    style={{
                                        fontSize:
                                            14,
                                    }}
                                >
                                    {
                                        student.full_name ||
                                        "Unnamed Student"
                                    }
                                </strong>

                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            12,
                                        marginTop:
                                            2,
                                    }}
                                >
                                    Admission No:
                                    {" "}
                                    {
                                        admissionNumber
                                    }
                                </Text>

                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            12,
                                        marginTop:
                                            1,
                                    }}
                                >
                                    Grade Class:
                                    {" "}
                                    {
                                        gradeClass
                                    }
                                </Text>
                            </div>
                        ),
                        student,
                    };
                }
            );
        },
        [
            students
        ]
    );

    const applyInstallmentBalance =
        useCallback(
            (
                studentId,
                studentClass,
                studentType,
                installment,
                paymentHistory,
                semester = null,
                excludePaymentId = null
            ) => {
                const information =
                    getInstallmentInformation(
                        studentId,
                        studentClass,
                        studentType,
                        installment,
                        paymentHistory,
                        semester,
                        excludePaymentId
                    );

                setOriginalInstallmentAmount(
                    information.originalAmount
                );

                setPreviouslyPaidAmount(
                    information.previouslyPaid
                );

                setMaximumInstallmentAmount(
                    information.remainingBalance
                );

                setFormData(
                    previous => ({
                        ...previous,
                        total_fee:
                            information.remainingBalance.toFixed(
                                2
                            )
                    })
                );

                form.setFieldsValue({
                    total_fee:
                        information.remainingBalance
                });

                if (!editId) {
                    form.setFieldsValue({
                        amount_paid:
                            undefined
                    });

                    setFormData(
                        previous => ({
                            ...previous,
                            amount_paid:
                                ""
                        })
                    );
                }
            },
            [
                getInstallmentInformation,
                form,
                editId
            ]
        );

    useEffect(
        () => {
            if (
                !openDialog ||
                !formData.student ||
                !formData.student_class ||
                !formData.student_type ||
                !formData.installment
            ) {
                return;
            }

            if (editId) {
                return;
            }

            applyInstallmentBalance(
                formData.student,
                formData.student_class,
                formData.student_type,
                formData.installment,
                selectedStudentPayments,
                formData.semester || null
            );
        },
        [
            openDialog,
            formData.student,
            formData.student_class,
            formData.student_type,
            formData.installment,
            formData.semester,
            selectedStudentPayments,
            editId,
            applyInstallmentBalance
        ]
    );

    const handleDialogOpen = async payment => {
        if (payment) {
            setEditId(
                payment.id
            );

            const selectedStudent =
                students.find(
                    student =>
                        Number(student.id) ===
                        Number(payment.student)
                );

            let history =
                payments.filter(
                    item =>
                        Number(
                            item.student
                        ) ===
                        Number(
                            payment.student
                        )
                );

            if (
                selectedYear &&
                payment.student
            ) {
                try {
                    const response =
                        await api.get(
                            "/api/users/payments/",
                            {
                                headers: {
                                    Authorization:
                                        `Bearer ${token}`
                                },
                                params: {
                                    academic_year:
                                        selectedYear.id,
                                    student_id:
                                        payment.student,
                                    page_size:
                                        1000
                                }
                            }
                        );

                    history =
                        (
                            response.data.results ||
                            response.data ||
                            []
                        ).filter(
                            item =>
                                Number(
                                    item.student
                                ) ===
                                Number(
                                    payment.student
                                )
                        );
                } catch (err) {
                    console.error(
                        "Could not load payment history for editing:",
                        err
                    );
                }
            }

            setSelectedStudentPayments(
                history
            );

            const paymentCurrency =
                getPaymentCurrency(
                    payment
                );

            const paymentData = {
                semester:
                    payment.semester,
                installment:
                    payment.installment,
                student:
                    selectedStudent?.full_name ||
                    "",
                student_class:
                    payment.student_class,
                student_type:
                    normalizeStudentType(
                        payment.student_type
                    ),
                total_fee:
                    Number(
                        payment.total_fee || 0
                    ),
                payer_name:
                    payment.payer_name,
                amount_paid:
                    Number(
                        payment.amount_paid || 0
                    ),
                payment_currency:
                    paymentCurrency,
                exchange_rate:
                    paymentCurrency === "USD"
                        ? Number(
                              payment.exchange_rate ||
                              0
                          )
                        : "",
                date:
                    payment.date
            };

            setFormData({
                ...paymentData,
                student:
                    payment.student
            });

            form.setFieldsValue(
                paymentData
            );

            const currentPaymentLrd =
                getPaymentAmountLrd(
                    payment
                );

            const maximum =
                calculateInstallmentMaximum(
                    payment.student,
                    payment.student_class,
                    payment.student_type,
                    payment.installment,
                    history,
                    payment.semester,
                    payment.id
                );

            const editableMaximum =
                Number(
                    (
                        maximum +
                        currentPaymentLrd
                    ).toFixed(2)
                );

            setMaximumInstallmentAmount(
                editableMaximum
            );

            setOriginalInstallmentAmount(
                editableMaximum
            );

            setPreviouslyPaidAmount(
                Math.max(
                    editableMaximum -
                    currentPaymentLrd,
                    0
                )
            );
        } else {
            setEditId(null);

            setSelectedStudentPayments([]);

            setOriginalInstallmentAmount(0);

            setPreviouslyPaidAmount(0);

            setMaximumInstallmentAmount(0);

            const emptyData = {
                semester: "",
                installment:
                    "1st Installment",
                student: "",
                student_class: "",
                student_type: "",
                total_fee: "",
                payer_name: "",
                amount_paid: "",
                payment_currency: "LRD",
                exchange_rate: "",
                date: ""
            };

            setFormData(
                emptyData
            );

            form.resetFields();

            form.setFieldsValue({
                installment:
                    "1st Installment",
                payment_currency:
                    "LRD"
            });
        }

        setOpenDialog(
            true
        );
    };

    const closeDialog = () => {
        setOpenDialog(false);

        setEditId(null);

        setMaximumInstallmentAmount(0);

        setOriginalInstallmentAmount(0);

        setPreviouslyPaidAmount(0);

        setSelectedStudentPayments([]);

        form.resetFields();

        setFormData({
            semester: "",
            installment:
                "1st Installment",
            student: "",
            student_class: "",
            student_type: "",
            total_fee: "",
            payer_name: "",
            amount_paid: "",
            payment_currency: "LRD",
            exchange_rate: "",
            date: ""
        });
    };

    const handleStudentSelect = async (
        value,
        option
    ) => {
        const student =
            option?.student;

        if (!student) {
            return;
        }

        const studentClass =
            student.grade_class || "";

        const localPreviousPayment =
            payments
                .filter(
                    payment =>
                        Number(
                            payment.student
                        ) ===
                        Number(
                            student.id
                        )
                )
                .sort(
                    (a, b) =>
                        Number(b.id) -
                        Number(a.id)
                )[0];

        let history = [];

        history =
            await fetchStudentPaymentHistory(
                student.id
            );

        const previousPayment =
            history.length > 0
                ? history
                    .slice()
                    .sort(
                        (a, b) =>
                            Number(b.id) -
                            Number(a.id)
                    )[0]
                : localPreviousPayment;

        const studentType =
            normalizeStudentType(
                previousPayment?.student_type
            );

        setFormData(
            previous => ({
                ...previous,
                student:
                    student.id,
                student_class:
                    studentClass,
                student_type:
                    studentType
            })
        );

        form.setFieldsValue({
            student:
                student.full_name,
            student_class:
                studentClass,
            student_type:
                studentType ||
                undefined
        });

        if (
            studentType &&
            studentClass
        ) {
            applyInstallmentBalance(
                student.id,
                studentClass,
                studentType,
                formData.installment ||
                    "1st Installment",
                history,
                formData.semester || null
            );
        }
    };

    const handleSemesterChange = value => {
        setFormData(
            previous => ({
                ...previous,
                semester:
                    value
            })
        );

        form.setFieldsValue({
            semester:
                value
        });

        if (
            formData.student &&
            formData.student_class &&
            formData.student_type &&
            formData.installment
        ) {
            applyInstallmentBalance(
                formData.student,
                formData.student_class,
                formData.student_type,
                formData.installment,
                selectedStudentPayments,
                value
            );
        }
    };

    const handleInstallmentChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    installment:
                        value,
                    amount_paid:
                        ""
                })
            );

            form.setFieldsValue({
                installment:
                    value,
                amount_paid:
                    undefined
            });

            if (
                formData.student &&
                formData.student_class &&
                formData.student_type
            ) {
                applyInstallmentBalance(
                    formData.student,
                    formData.student_class,
                    formData.student_type,
                    value,
                    selectedStudentPayments,
                    formData.semester || null
                );
            }
        };

    const handleClassChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    student_class:
                        value
                })
            );

            form.setFieldsValue({
                student_class:
                    value
            });

            if (
                formData.student &&
                formData.student_type &&
                formData.installment
            ) {
                applyInstallmentBalance(
                    formData.student,
                    value,
                    formData.student_type,
                    formData.installment,
                    selectedStudentPayments,
                    formData.semester || null
                );
            }
        };

    const handleStudentTypeChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    student_type:
                        value
                })
            );

            form.setFieldsValue({
                student_type:
                    value
            });

            if (
                formData.student &&
                formData.student_class &&
                formData.installment
            ) {
                applyInstallmentBalance(
                    formData.student,
                    formData.student_class,
                    value,
                    formData.installment,
                    selectedStudentPayments,
                    formData.semester || null
                );
            }
        };

    const handleCurrencyChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    payment_currency:
                        value,
                    exchange_rate:
                        value === "USD"
                            ? previous.exchange_rate
                            : ""
                })
            );

            form.setFieldsValue({
                payment_currency:
                    value,
                exchange_rate:
                    value === "USD"
                        ? form.getFieldValue(
                              "exchange_rate"
                          )
                        : undefined
            });

            const currentAmount =
                Number(
                    form.getFieldValue(
                        "amount_paid"
                    ) || 0
                );

            if (
                value === "USD"
            ) {
                return;
            }

            if (
                currentAmount > 0
            ) {
                handleAmountChange(
                    currentAmount,
                    "LRD"
                );
            }
        };

    const handleExchangeRateChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    exchange_rate:
                        value || ""
                })
            );

            form.setFieldsValue({
                exchange_rate:
                    value
            });
        };

    const handleAmountChange =
        (
            value,
            forcedCurrency = null
        ) => {
            if (
                value === null ||
                value === undefined
            ) {
                setFormData(
                    previous => ({
                        ...previous,
                        amount_paid:
                            ""
                    })
                );

                return;
            }

            const enteredAmount =
                Number(value);

            const currency =
                forcedCurrency ||
                form.getFieldValue(
                    "payment_currency"
                ) ||
                formData.payment_currency ||
                "LRD";

            const exchangeRate =
                form.getFieldValue(
                    "exchange_rate"
                ) ||
                formData.exchange_rate;

            const enteredAmountLrd =
                convertToLrd(
                    enteredAmount,
                    currency,
                    exchangeRate
                );

            const maximum =
                Number(
                    maximumInstallmentAmount
                );

            if (
                currency === "USD" &&
                Number(exchangeRate || 0) <= 0
            ) {
                setFormData(
                    previous => ({
                        ...previous,
                        amount_paid:
                            value
                    })
                );

                return;
            }

            if (
                maximum >= 0 &&
                enteredAmountLrd >
                maximum
            ) {
                const maximumInCurrency =
                    currency === "USD"
                        ? Number(
                              (
                                  maximum /
                                  Number(
                                      exchangeRate
                                  )
                              ).toFixed(2)
                          )
                        : maximum;

                Swal.fire({
                    icon:
                        "error",
                    title:
                        "Payment Amount Exceeded",
                    html: `
                        <div
                            style="
                                font-size:15px;
                                line-height:1.8;
                            "
                        >
                            <div>
                                The amount entered
                                exceeds the student's
                                current installment balance.
                            </div>

                            <br/>

                            <strong>
                                Installment:
                            </strong>

                            ${formData.installment}

                            <br/>

                            <strong>
                                Current balance:
                            </strong>

                            ${formatMoney(
                                maximum
                            )} LRD

                            <br/>

                            <strong>
                                Maximum in ${currency}:
                            </strong>

                            ${formatMoney(
                                maximumInCurrency
                            )} ${currency}

                            <br/>

                            <strong>
                                Amount entered:
                            </strong>

                            ${formatMoney(
                                enteredAmount
                            )} ${currency}

                            ${
                                currency === "USD"
                                    ? `
                                        <br/>

                                        <strong>
                                            Exchange rate:
                                        </strong>

                                        ${formatMoney(
                                            exchangeRate
                                        )} LRD/USD
                                    `
                                    : ""
                            }
                        </div>
                    `,
                    confirmButtonColor:
                        "#ff6b00"
                });

                form.setFieldsValue({
                    amount_paid:
                        maximumInCurrency
                });

                setFormData(
                    previous => ({
                        ...previous,
                        amount_paid:
                            maximumInCurrency
                    })
                );

                return;
            }

            setFormData(
                previous => ({
                    ...previous,
                    amount_paid:
                        value
                })
            );
        };

    const handleSubmit =
        async values => {
            if (!token) {
                Swal.fire({
                    icon:
                        "error",
                    title:
                        "Authentication Required",
                    text:
                        "No authentication token was found.",
                    confirmButtonColor:
                        "#ff6b00"
                });

                return;
            }

            if (!selectedYear) {
                Swal.fire({
                    icon:
                        "error",
                    title:
                        "Academic Year Required",
                    text:
                        "Please select an academic year.",
                    confirmButtonColor:
                        "#ff6b00"
                });

                return;
            }

            try {
                setLoading(true);

                const selectedStudent =
                    students.find(
                        student =>
                            student.full_name ===
                                values.student ||
                            Number(student.id) ===
                                Number(values.student)
                    );

                const studentId =
                    selectedStudent?.id ||
                    formData.student;

                if (!studentId) {
                    Swal.fire({
                        icon:
                            "error",
                        title:
                            "Student Required",
                        text:
                            "Please select a valid student.",
                        confirmButtonColor:
                            "#ff6b00"
                    });

                    setLoading(false);

                    return;
                }

                let currentHistory =
                    selectedStudentPayments;

                if (!editId) {
                    currentHistory =
                        await fetchStudentPaymentHistory(
                            studentId
                        );
                }

                const studentClass =
                    Number(
                        values.student_class ||
                        formData.student_class
                    );

                const studentType =
                    normalizeStudentType(
                        values.student_type ||
                        formData.student_type
                    );

                const installment =
                    values.installment ||
                    formData.installment;

                const semester =
                    values.semester ||
                    formData.semester;

                const paymentCurrency =
                    String(
                        values.payment_currency ||
                        formData.payment_currency ||
                        "LRD"
                    ).toUpperCase();

                const exchangeRate =
                    Number(
                        values.exchange_rate ||
                        formData.exchange_rate ||
                        0
                    );

                const amountPaid =
                    Number(
                        values.amount_paid ||
                        0
                    );

                if (
                    paymentCurrency === "USD" &&
                    (
                        !Number.isFinite(
                            exchangeRate
                        ) ||
                        exchangeRate <= 0
                    )
                ) {
                    Swal.fire({
                        icon:
                            "error",
                        title:
                            "Exchange Rate Required",
                        text:
                            "Please enter a valid USD to LRD exchange rate.",
                        confirmButtonColor:
                            "#ff6b00"
                    });

                    setLoading(false);

                    return;
                }

                if (
                    amountPaid <= 0
                ) {
                    Swal.fire({
                        icon:
                            "error",
                        title:
                            "Invalid Amount",
                        text:
                            "Please enter an amount greater than zero.",
                        confirmButtonColor:
                            "#ff6b00"
                    });

                    setLoading(false);

                    return;
                }

                const amountPaidLrd =
                    convertToLrd(
                        amountPaid,
                        paymentCurrency,
                        exchangeRate
                    );

                let installmentLimit =
                    calculateInstallmentMaximum(
                        studentId,
                        studentClass,
                        studentType,
                        installment,
                        currentHistory,
                        semester,
                        editId
                    );

                if (
                    editId
                ) {
                    const existingPayment =
                        currentHistory.find(
                            payment =>
                                Number(
                                    payment.id
                                ) ===
                                Number(
                                    editId
                                )
                        );

                    if (
                        existingPayment
                    ) {
                        installmentLimit =
                            Number(
                                (
                                    installmentLimit +
                                    getPaymentAmountLrd(
                                        existingPayment
                                    )
                                ).toFixed(2)
                            );
                    }
                }

                if (
                    installmentLimit <= 0 &&
                    !editId
                ) {
                    Swal.fire({
                        icon:
                            "warning",
                        title:
                            "No Balance Remaining",
                        text:
                            "This installment has already been fully paid. There is no remaining balance to post.",
                        confirmButtonColor:
                            "#ff6b00"
                    });

                    setLoading(false);

                    return;
                }

                if (
                    amountPaidLrd >
                    installmentLimit
                ) {
                    const maximumInCurrency =
                        paymentCurrency === "USD"
                            ? Number(
                                  (
                                      installmentLimit /
                                      exchangeRate
                                  ).toFixed(2)
                              )
                            : installmentLimit;

                    Swal.fire({
                        icon:
                            "error",
                        title:
                            "Payment Amount Exceeded",
                        html: `
                            <div
                                style="
                                    font-size:15px;
                                    line-height:1.8;
                                "
                            >
                                <div>
                                    This payment cannot
                                    be greater than the
                                    student's current
                                    balance.
                                </div>

                                <br/>

                                <strong>
                                    Installment:
                                </strong>

                                ${installment}

                                <br/>

                                <strong>
                                    Current balance:
                                </strong>

                                ${formatMoney(
                                    installmentLimit
                                )} LRD

                                <br/>

                                <strong>
                                    Maximum payment:
                                </strong>

                                ${formatMoney(
                                    maximumInCurrency
                                )} ${paymentCurrency}

                                <br/>

                                <strong>
                                    Amount entered:
                                </strong>

                                ${formatMoney(
                                    amountPaid
                                )} ${paymentCurrency}

                                <br/>

                                <strong>
                                    LRD equivalent:
                                </strong>

                                ${formatMoney(
                                    amountPaidLrd
                                )} LRD

                                ${
                                    paymentCurrency === "USD"
                                        ? `
                                            <br/>

                                            <strong>
                                                Exchange rate:
                                            </strong>

                                            ${formatMoney(
                                                exchangeRate
                                            )} LRD/USD
                                        `
                                        : ""
                                }
                            </div>
                        `,
                        confirmButtonColor:
                            "#ff6b00"
                    });

                    setLoading(false);

                    return;
                }

                const payload = {
                    ...values,
                    student:
                        Number(
                            studentId
                        ),
                    academic_year:
                        selectedYear.id,
                    student_class:
                        studentClass,
                    student_type:
                        studentType,
                    installment:
                        installment,
                    semester:
                        semester,
                    amount_paid:
                        amountPaid,
                    payment_currency:
                        paymentCurrency,
                    exchange_rate:
                        paymentCurrency === "USD"
                            ? exchangeRate
                            : null
                };

                if (!editId) {
                    payload.total_fee =
                        Number(
                            installmentLimit.toFixed(2)
                        );
                }

                if (editId) {
                    await api.put(
                        `/api/users/payments/${editId}/`,
                        payload,
                        {
                            headers: {
                                Authorization:
                                    `Bearer ${token}`
                            }
                        }
                    );

                    await Swal.fire({
                        icon:
                            "success",
                        title:
                            "Payment Updated",
                        text:
                            "The payment record was updated successfully.",
                        confirmButtonColor:
                            "#ff6b00",
                        timer:
                            1800,
                        timerProgressBar:
                            true
                    });
                } else {
                    await api.post(
                        "/api/users/payments/",
                        payload,
                        {
                            headers: {
                                Authorization:
                                    `Bearer ${token}`
                            }
                        }
                    );

                    await Swal.fire({
                        icon:
                            "success",
                        title:
                            "Payment Saved",
                        text:
                            "The payment record was added successfully.",
                        confirmButtonColor:
                            "#ff6b00",
                        timer:
                            1800,
                        timerProgressBar:
                            true
                    });
                }

                closeDialog();

                await fetchPayments();
            } catch (err) {
                console.error(
                    err
                );

                const responseData =
                    err?.response?.data;

                let message =
                    "Failed to save the payment record.";

                if (
                    responseData?.amount_paid
                ) {
                    message =
                        Array.isArray(
                            responseData.amount_paid
                        )
                            ? responseData.amount_paid[0]
                            : responseData.amount_paid;
                } else if (
                    responseData?.exchange_rate
                ) {
                    message =
                        Array.isArray(
                            responseData.exchange_rate
                        )
                            ? responseData.exchange_rate[0]
                            : responseData.exchange_rate;
                } else if (
                    responseData?.payment_currency
                ) {
                    message =
                        Array.isArray(
                            responseData.payment_currency
                        )
                            ? responseData.payment_currency[0]
                            : responseData.payment_currency;
                } else if (
                    responseData?.total_fee
                ) {
                    message =
                        Array.isArray(
                            responseData.total_fee
                        )
                            ? responseData.total_fee[0]
                            : responseData.total_fee;
                } else if (
                    responseData?.detail
                ) {
                    message =
                        Array.isArray(
                            responseData.detail
                        )
                            ? responseData.detail[0]
                            : responseData.detail;
                } else if (
                    responseData?.non_field_errors
                ) {
                    message =
                        Array.isArray(
                            responseData.non_field_errors
                        )
                            ? responseData.non_field_errors[0]
                            : responseData.non_field_errors;
                } else {
                    const firstErrorKey =
                        responseData &&
                        Object.keys(
                            responseData
                        )[0];

                    if (
                        firstErrorKey
                    ) {
                        const firstError =
                            responseData[
                                firstErrorKey
                            ];

                        message =
                            Array.isArray(
                                firstError
                            )
                                ? firstError[0]
                                : firstError;
                    }
                }

                Swal.fire({
                    icon:
                        "error",
                    title:
                        "Invalid Payment",
                    text:
                        message,
                    confirmButtonColor:
                        "#ff6b00"
                });
            } finally {
                setLoading(false);
            }
        };

    const handleDelete = async id => {
        const result = await Swal.fire({
            title: "Delete Payment Record?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#ff6b00",
            cancelButtonColor: "#111111"
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            setLoading(true);

            await api.delete(
                `/api/users/payments/${id}/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            await Swal.fire({
                icon: "success",
                title: "Deleted",
                text: "Payment record deleted successfully.",
                confirmButtonColor: "#ff6b00",
                timer: 1600,
                timerProgressBar: true
            });

            fetchPayments();
        } catch (err) {
            console.error(err);

            Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text: "The payment record could not be deleted.",
                confirmButtonColor: "#ff6b00"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = () => {
        generatePaymentPDF({
            selectedGrade,
            grades,
            token,
            api,
            setLoading,
            setError
        });
    };

    const resetFilters = () => {
        setSearch("");
        setSelectedGrade("");
        setPage(1);
    };

    const columns = [
        {
            title: "PAYMENT TYPE",
            dataIndex: "installment",
            key: "installment",
            fixed: "left",
            width: 175,
            render: value => (
                <Tag
                    color="orange"
                    style={{
                        fontWeight: 700,
                        borderRadius: 20,
                        padding: "5px 13px"
                    }}
                >
                    {value || "N/A"}
                </Tag>
            )
        },
        {
            title: "STUDENT",
            dataIndex: "student_name",
            key: "student_name",
            width: 220,
            render: value => (
                <Text strong>
                    {value || "N/A"}
                </Text>
            )
        },
        {
            title: "SEMESTER",
            dataIndex: "semester",
            key: "semester",
            width: 120
        },
        {
            title: "CLASS",
            dataIndex: "student_class_name",
            key: "student_class_name",
            width: 170
        },
        {
            title: "TOTAL FEE",
            dataIndex: "total_fee",
            key: "total_fee",
            width: 150,
            render: value => (
                <Text strong>
                    {formatMoney(value)} LRD
                </Text>
            )
        },
        {
            title: "AMOUNT PAID",
            dataIndex: "amount_paid",
            key: "amount_paid",
            width: 190,
            render: (value, record) => {
                const currency =
                    getPaymentCurrency(
                        record
                    );

                const amount =
                    Number(
                        value || 0
                    );

                const amountLrd =
                    getPaymentAmountLrd(
                        record
                    );

                return (
                    <div>
                        <Text
                            strong
                            style={{
                                color: "#16803c"
                            }}
                        >
                            {formatMoney(
                                amount
                            )}{" "}
                            {currency}
                        </Text>

                        {currency === "USD" && (
                            <div>
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11
                                    }}
                                >
                                    ≈{" "}
                                    {formatMoney(
                                        amountLrd
                                    )} LRD
                                </Text>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "EXCHANGE RATE",
            dataIndex: "exchange_rate",
            key: "exchange_rate",
            width: 150,
            render: (value, record) => {
                const currency =
                    getPaymentCurrency(
                        record
                    );

                if (
                    currency !== "USD"
                ) {
                    return (
                        <Text type="secondary">
                            —
                        </Text>
                    );
                }

                return (
                    <Tag
                        color="blue"
                        style={{
                            fontWeight: 600
                        }}
                    >
                        {formatMoney(
                            value
                        )} LRD/USD
                    </Tag>
                );
            }
        },
{
    title: "PAYMENT BALANCE",
    dataIndex: "balance_fee",
    key: "balance_fee",
    align: "right",
    render: (value) => (
        <span style={{ fontWeight: 700 }}>
            {Number(value || 0).toLocaleString(
                undefined,
                {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }
            )} LRD
        </span>
    ),
},

        {
            title: "PAYER",
            dataIndex: "payer_name",
            key: "payer_name",
            width: 200
        },
        {
            title: "ACTIONS",
            key: "actions",
            fixed: "right",
            width: 130,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Payment">
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() =>
                                handleDialogOpen(record)
                            }
                            style={{
                                background: "#111111",
                                borderColor: "#111111"
                            }}
                        />
                    </Tooltip>

                    <Popconfirm
                        title="Delete this payment?"
                        description="This action cannot be undone."
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{
                            danger: true
                        }}
                        onConfirm={() =>
                            handleDelete(record.id)
                        }
                    >
                        <Tooltip title="Delete Payment">
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const selectedCurrency =
        formData.payment_currency ||
        "LRD";

    const currentExchangeRate =
        Number(
            formData.exchange_rate ||
            0
        );

    const currentEnteredAmount =
        Number(
            formData.amount_paid ||
            0
        );

    const currentAmountLrd =
        convertToLrd(
            currentEnteredAmount,
            selectedCurrency,
            currentExchangeRate
        );

    return (
        <div
            className="financial-payment-page"
            style={{
                minHeight: "100vh",
                padding: "24px",
                background:
                    "linear-gradient(135deg,#f5f5f5 0%,#eeeeee 100%)"
            }}
        >
            <Card
                bordered={false}
                style={{
                    marginBottom: 24,
                    borderRadius: 18,
                    background:
                        "linear-gradient(135deg,#111111 0%,#222222 60%,#ff6b00 100%)",
                    boxShadow:
                        "0 12px 30px rgba(0,0,0,0.15)"
                }}
            >
                <Row
                    justify="space-between"
                    align="middle"
                    gutter={[16, 16]}
                >
                    <Col xs={24} md={12}>
                        <Title
                            level={2}
                            style={{
                                color: "#ffffff",
                                margin: 0,
                                fontWeight: 800
                            }}
                        >
                            <DollarOutlined
                                style={{
                                    color: "#ff7900",
                                    marginRight: 10
                                }}
                            />
                            Financial Payment Records
                        </Title>

                        <Text
                            style={{
                                color: "#dddddd",
                                fontSize: 14
                            }}
                        >
                            Manage, monitor, and track student fee payments securely.
                        </Text>
                    </Col>

                    <Col xs={24} md={12}>
                        <Space
                            wrap
                            style={{
                                width: "100%",
                                justifyContent: "flex-end"
                            }}
                        >
                            <Tag
                                color="orange"
                                style={{
                                    padding: "7px 14px",
                                    borderRadius: 20,
                                    fontWeight: 700
                                }}
                            >
                                Academic Year:{" "}
                                {selectedYear?.name || "Not Selected"}
                            </Tag>

                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadPDF}
                                style={{
                                    background: "#ff6b00",
                                    borderColor: "#ff6b00",
                                    color: "#ffffff",
                                    fontWeight: 700,
                                    height: 42,
                                    borderRadius: 10
                                }}
                            >
                                Download PDF
                            </Button>

                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => handleDialogOpen()}
                                style={{
                                    background: "#ffffff",
                                    borderColor: "#ffffff",
                                    color: "#111111",
                                    fontWeight: 700,
                                    height: 42,
                                    borderRadius: 10
                                }}
                            >
                                Add Payment
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Row
                gutter={[16, 16]}
                style={{
                    marginBottom: 24
                }}
            >
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        style={{
                            borderRadius: 16,
                            borderLeft: "5px solid #ff6b00"
                        }}
                    >
                        <Statistic
                            title="TOTAL FEES (LRD)"
                            value={Number(
                                financialSummary.total_fee || 0
                            )}
                            suffix="LRD"
                            precision={2}
                            valueStyle={{
                                fontWeight: 800
                            }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        style={{
                            borderRadius: 16,
                            borderLeft: "5px solid #16803c"
                        }}
                    >
                        <Statistic
                            title="TOTAL COLLECTED (LRD)"
                            value={Number(
                                financialSummary.total_paid_lrd ||
                                financialSummary.total_paid ||
                                0
                            )}
                            suffix="LRD"
                            precision={2}
                            valueStyle={{
                                color: "#16803c",
                                fontWeight: 800
                            }}
                        />
                    </Card>
                </Col>

<Col xs={24} sm={12} lg={8}>
    <Card
        bordered={false}
        style={{
            borderRadius: 16,
            borderLeft: "5px solid #d4380d",
        }}
    >
        <Statistic
            title="OUTSTANDING BALANCE (LRD)"
            value={Number(
                financialSummary?.total_balance ?? 0
            )}
            suffix="LRD"
            precision={2}
            valueStyle={{
                color: "#d4380d",
                fontWeight: 800,
            }}
        />
    </Card>
</Col>

            </Row>

            <Card
                bordered={false}
                style={{
                    marginBottom: 20,
                    borderRadius: 16
                }}
                bodyStyle={{
                    padding: 18
                }}
            >
                <Row
                    gutter={[16, 16]}
                    align="middle"
                >
                    <Col xs={24} md={10} lg={8}>
                        <Input
                            allowClear
                            size="large"
                            prefix={
                                <SearchOutlined
                                    style={{
                                        color: "#ff6b00"
                                    }}
                                />
                            }
                            placeholder="Search student name..."
                            value={search}
                            onChange={event => {
                                setSearch(event.target.value);
                                setPage(1);
                            }}
                            style={{
                                borderRadius: 10
                            }}
                        />
                    </Col>

                    <Col xs={24} md={8} lg={6}>
                        <Select
                            allowClear
                            size="large"
                            placeholder="Filter by Grade Class"
                            value={selectedGrade || undefined}
                            onChange={value => {
                                setSelectedGrade(value || "");
                                setPage(1);
                            }}
                            style={{
                                width: "100%"
                            }}
                            suffixIcon={<FilterOutlined />}
                        >
                            {grades.map(grade => (
                                <Option
                                    key={grade.id}
                                    value={grade.id}
                                >
                                    {grade.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} md={6} lg={4}>
                        <Button
                            block
                            size="large"
                            icon={<ReloadOutlined />}
                            onClick={resetFilters}
                            style={{
                                borderRadius: 10,
                                fontWeight: 600
                            }}
                        >
                            Reset Filters
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Card
                bordered={false}
                style={{
                    borderRadius: 18,
                    overflow: "hidden"
                }}
                bodyStyle={{
                    padding: 0
                }}
            >
                <Table
                    rowKey="id"
                    loading={{
                        spinning:
                            loading ||
                            supportingDataLoading,
                        indicator: <Spin size="large" />
                    }}
                    columns={columns}
                    dataSource={payments}
                    scroll={{
                        x: 1800
                    }}
                    pagination={{
                        current: page,
                        pageSize: rowsPerPage,
                        total: totalPayments,
                        showSizeChanger: true,
                        pageSizeOptions: [
                            "10",
                            "25",
                            "50",
                            "100"
                        ],
                        showTotal: total =>
                            `Total ${total} payment records`,
                        onChange: (
                            currentPage,
                            currentPageSize
                        ) => {
                            setPage(currentPage);
                            setRowsPerPage(currentPageSize);
                        }
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={
                                    Empty.PRESENTED_IMAGE_SIMPLE
                                }
                                description="No payment records found"
                            />
                        )
                    }}
                    rowClassName={() =>
                        "payment-table-row"
                    }
                />
            </Card>

            <Modal
                open={openDialog}
                title={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10
                        }}
                    >
                        <div
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 12,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#111111",
                                color: "#ff6b00"
                            }}
                        >
                            <WalletOutlined />
                        </div>

                        <div>
                            <div
                                style={{
                                    fontSize: 18,
                                    fontWeight: 800
                                }}
                            >
                                {editId
                                    ? "Edit Payment Record"
                                    : "Add Payment Record"}
                            </div>

                            <Text type="secondary">
                                Enter the student's payment information
                            </Text>
                        </div>
                    </div>
                }
                centered
                width={650}
                destroyOnClose
                maskClosable={false}
                closable={false}
                footer={null}
                styles={{
                    body: {
                        padding: 0
                    }
                }}
            >
                <Divider
                    style={{
                        margin: "12px 0 0"
                    }}
                />

                <div
                    style={{
                        maxHeight: "65vh",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "18px 24px 10px 24px"
                    }}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <Form.Item
                            label="Student"
                            name="student"
                            rules={[
                                {
                                    required: true,
                                    message: "Please select a student"
                                }
                            ]}
                        >
                            <AutoComplete
                                size="large"
                                options={studentOptions}
                                placeholder="Search and select student"
                                disabled={Boolean(editId)}
                                filterOption={(inputValue, option) => {
                                    const student = option?.student;
                                    const search =
                                        inputValue.trim().toLowerCase();

                                    if (!student) return false;

                                    const searchableText = [
                                        student.full_name,
                                        student.admission_id,
                                        student.grade_class_name,
                                        student.grade_class?.name,
                                        student.grade_class
                                    ]
                                        .filter(Boolean)
                                        .join(" ")
                                        .toLowerCase();

                                    return searchableText.includes(search);
                                }}
                                onSelect={handleStudentSelect}
                                allowClear
                            />
                        </Form.Item>

                        {!editId &&
                            formData.student &&
                            selectedStudentPayments.length > 0 && (
                                <Alert
                                    type="success"
                                    showIcon
                                    icon={<CheckCircleOutlined />}
                                    message="Previous payment found"
                                    description={
                                        <span>
                                            This student already has{" "}
                                            <strong>
                                                {selectedStudentPayments.length}
                                            </strong>{" "}
                                            payment record
                                            {selectedStudentPayments.length === 1
                                                ? ""
                                                : "s"}{" "}
                                            for this academic year.
                                            Previous payments are converted
                                            to LRD for balance calculation.
                                        </span>
                                    }
                                    style={{
                                        marginBottom: 18,
                                        borderRadius: 10
                                    }}
                                />
                            )}

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Semester"
                                    name="semester"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Select semester"
                                        }
                                    ]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Select semester"
                                        onChange={
                                            handleSemesterChange
                                        }
                                    >
                                        <Option value="First">
                                            First Semester
                                        </Option>
                                        <Option value="Second">
                                            Second Semester
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label={
                                        <Space size={6}>
                                            <span>Installment</span>
                                            {editId && (
                                                <Tooltip title="Installment cannot be changed while editing this payment.">
                                                    <LockOutlined
                                                        style={{
                                                            color: "#ff6b00"
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Space>
                                    }
                                    name="installment"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Select installment"
                                        }
                                    ]}
                                >
                                    <Select
                                        size="large"
                                        disabled={Boolean(editId)}
                                        onChange={
                                            handleInstallmentChange
                                        }
                                    >
                                        <Option value="Registration">
                                            Registration
                                        </Option>
                                        <Option value="1st Installment">
                                            1st Installment
                                        </Option>
                                        <Option value="2nd Installment">
                                            2nd Installment
                                        </Option>
                                        <Option value="3rd Installment">
                                            3rd Installment
                                        </Option>
                                        <Option value="4th Installment">
                                            4th Installment
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        {formData.student &&
                            maximumInstallmentAmount >= 0 &&
                            formData.student_type &&
                            formData.student_class && (
                                <Card
                                    size="small"
                                    style={{
                                        marginBottom: 18,
                                        borderRadius: 12,
                                        background:
                                            (
                                                editId
                                                    ? Number(
                                                          formData.total_fee || 0
                                                      )
                                                    : Number(
                                                          maximumInstallmentAmount || 0
                                                      )
                                            ) > 0
                                                ? "#fff7ed"
                                                : "#fff1f0",
                                        border:
                                            (
                                                editId
                                                    ? Number(
                                                          formData.total_fee || 0
                                                      )
                                                    : Number(
                                                          maximumInstallmentAmount || 0
                                                      )
                                            ) > 0
                                                ? "1px solid #fed7aa"
                                                : "1px solid #ffa39e"
                                    }}
                                >
                                    <Row gutter={[12, 12]}>
                                        <Col xs={24} sm={8}>
                                            <Text type="secondary">
                                                Original Fee
                                            </Text>
                                            <br />
                                            <strong>
                                                {formatMoney(
                                                    editId
                                                        ? Number(
                                                              formData.total_fee || 0
                                                          )
                                                        : Number(
                                                              originalInstallmentAmount || 0
                                                          )
                                                )}{" "}
                                                LRD
                                            </strong>
                                        </Col>

                                        <Col xs={24} sm={8}>
                                            <Text type="secondary">
                                                Previously Paid
                                            </Text>
                                            <br />
                                            <strong
                                                style={{
                                                    color: "#16803c"
                                                }}
                                            >
                                                {formatMoney(
                                                    editId
                                                        ? previouslyPaidAmount
                                                        : previouslyPaidAmount
                                                )}{" "}
                                                LRD
                                            </strong>
                                        </Col>

                                        <Col xs={24} sm={8}>
                                            <Text type="secondary">
                                                Current Balance
                                            </Text>
                                            <br />
                                            <strong
                                                style={{
                                                    color:
                                                        (
                                                            Number(
                                                                maximumInstallmentAmount || 0
                                                            )
                                                        ) > 0
                                                            ? "#c2410c"
                                                            : "#cf1322",
                                                    fontSize: 18
                                                }}
                                            >
                                                {formatMoney(
                                                    editId
                                                        ? Math.max(
                                                              Number(
                                                                  formData.total_fee || 0
                                                              ) -
                                                                  previouslyPaidAmount,
                                                              0
                                                          )
                                                        : Number(
                                                              maximumInstallmentAmount || 0
                                                          )
                                                )}{" "}
                                                LRD
                                            </strong>
                                        </Col>
                                    </Row>
                                </Card>
                            )}

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Class"
                                    name="student_class"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Select class"
                                        }
                                    ]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Select class"
                                        disabled={Boolean(editId)}
                                        onChange={
                                            handleClassChange
                                        }
                                    >
                                        {grades.map(grade => (
                                            <Option
                                                key={grade.id}
                                                value={grade.id}
                                            >
                                                {grade.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Student Type"
                                    name="student_type"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Select student type"
                                        }
                                    ]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Select student type"
                                        disabled={Boolean(editId)}
                                        onChange={
                                            handleStudentTypeChange
                                        }
                                    >
                                        <Option value="old">
                                            Old Student
                                        </Option>
                                        <Option value="new">
                                            New Student
                                        </Option>
                                        <Option value="scholarship">
                                            Scholarship Student
                                        </Option>
                                        <Option value="ward">
                                            Ward Or Dependant Student
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label={
                                        editId
                                            ? "Original Fee / Total Fee"
                                            : "Installment Cost / Total Fee"
                                    }
                                    name="total_fee"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Fee amount is required"
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        size="large"
                                        min={0}
                                        precision={2}
                                        suffix="LRD"
                                        disabled
                                        style={{
                                            width: "100%"
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Payment Currency"
                                    name="payment_currency"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Select payment currency"
                                        }
                                    ]}
                                >
                                    <Select
                                        size="large"
                                        onChange={
                                            handleCurrencyChange
                                        }
                                    >
                                        <Option value="LRD">
                                            Liberian Dollar (LRD)
                                        </Option>
                                        <Option value="USD">
                                            United States Dollar (USD)
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Amount Paid"
                                    name="amount_paid"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Enter amount paid"
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        size="large"
                                        min={0}
                                        precision={2}
                                        prefix={
                                            selectedCurrency === "USD"
                                                ? "$"
                                                : "LD"
                                        }
                                        style={{
                                            width: "100%"
                                        }}
                                        placeholder={
                                            selectedCurrency === "USD"
                                                ? "Enter USD amount"
                                                : "Enter LRD amount"
                                        }
                                        disabled={
                                            maximumInstallmentAmount <= 0
                                        }
                                        onChange={
                                            handleAmountChange
                                        }
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="USD to LRD Exchange Rate"
                                    name="exchange_rate"
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                const currency =
                                                    form.getFieldValue(
                                                        "payment_currency"
                                                    );

                                                if (
                                                    currency !==
                                                    "USD"
                                                ) {
                                                    return Promise.resolve();
                                                }

                                                if (
                                                    value === undefined ||
                                                    value === null ||
                                                    value === "" ||
                                                    Number(value) <= 0
                                                ) {
                                                    return Promise.reject(
                                                        new Error(
                                                            "Enter the current USD to LRD exchange rate"
                                                        )
                                                    );
                                                }

                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        size="large"
                                        min={0}
                                        precision={4}
                                        suffix="LRD/USD"
                                        style={{
                                            width: "100%"
                                        }}
                                        placeholder="Example: 170"
                                        disabled={
                                            selectedCurrency !==
                                            "USD"
                                        }
                                        onChange={
                                            handleExchangeRateChange
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {selectedCurrency === "USD" &&
                            currentExchangeRate > 0 &&
                            currentEnteredAmount > 0 && (
                                <Alert
                                    type="success"
                                    showIcon
                                    icon={<DollarOutlined />}
                                    message="USD payment conversion"
                                    description={
                                        <>
                                            <strong>
                                                {formatMoney(
                                                    currentEnteredAmount
                                                )} USD
                                            </strong>
                                            {" × "}
                                            <strong>
                                                {formatMoney(
                                                    currentExchangeRate
                                                )} LRD/USD
                                            </strong>
                                            {" = "}
                                            <strong>
                                                {formatMoney(
                                                    currentAmountLrd
                                                )} LRD
                                            </strong>
                                        </>
                                    }
                                    style={{
                                        marginBottom: 20,
                                        borderRadius: 10
                                    }}
                                />
                            )}

                        <Form.Item
                            label="Payer Name"
                            name="payer_name"
                            rules={[
                                {
                                    required: true,
                                    message: "Enter payer name"
                                }
                            ]}
                        >
                            <Input
                                size="large"
                                placeholder="Enter name of person making payment"
                            />
                        </Form.Item>

                        {maximumInstallmentAmount > 0 && (
                            <Alert
                                type="info"
                                showIcon
                                icon={<WarningOutlined />}
                                message={
                                    editId
                                        ? "Editing payment amount"
                                        : "Current installment balance"
                                }
                                description={
                                    editId ? (
                                        <>
                                            This payment is being evaluated
                                            against the installment in LRD.
                                            Previous paid amount:
                                            {" "}
                                            <strong>
                                                {formatMoney(
                                                    previouslyPaidAmount
                                                )}{" "}
                                                LRD
                                            </strong>
                                            . Current editable balance:
                                            {" "}
                                            <strong>
                                                {formatMoney(
                                                    maximumInstallmentAmount
                                                )}{" "}
                                                LRD
                                            </strong>
                                            .
                                        </>
                                    ) : (
                                        <>
                                            The student's current balance for{" "}
                                            <strong>
                                                {formData.installment}
                                            </strong>{" "}
                                            is{" "}
                                            <strong>
                                                {formatMoney(
                                                    maximumInstallmentAmount
                                                )}{" "}
                                                LRD
                                            </strong>
                                            .
                                        </>
                                    )
                                }
                                style={{
                                    marginBottom: 20,
                                    borderRadius: 10
                                }}
                            />
                        )}

                        {formData.student &&
                            formData.student_type &&
                            maximumInstallmentAmount === 0 &&
                            !editId && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="No remaining balance"
                                    description={
                                        <>
                                            This student has already
                                            fully paid the{" "}
                                            <strong>
                                                {formData.installment}
                                            </strong>
                                            . No additional payment can
                                            be posted for this installment.
                                        </>
                                    }
                                    style={{
                                        marginBottom: 20,
                                        borderRadius: 10
                                    }}
                                />
                            )}

                        <Divider />

                        <Space
                            style={{
                                width: "100%",
                                justifyContent: "flex-end"
                            }}
                        >
                            <Button
                                size="large"
                                onClick={closeDialog}
                            >
                                Cancel
                            </Button>

                            <Button
                                size="large"
                                htmlType="submit"
                                type="primary"
                                loading={
                                    loading ||
                                    studentPaymentLoading
                                }
                                disabled={
                                    !editId &&
                                    maximumInstallmentAmount <= 0
                                }
                                icon={
                                    editId ? (
                                        <EditOutlined />
                                    ) : (
                                        <PlusOutlined />
                                    )
                                }
                                style={{
                                    background: "#ff6b00",
                                    borderColor: "#ff6b00",
                                    fontWeight: 700
                                }}
                            >
                                {editId
                                    ? "Update Payment"
                                    : "Save Payment"}
                            </Button>
                        </Space>
                    </Form>
                </div>
            </Modal>

            {error && (
                <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError("")}
                    style={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        zIndex: 9999,
                        maxWidth: 420,
                        borderRadius: 10
                    }}
                />
            )}

            {success && (
                <Alert
                    message={success}
                    type="success"
                    showIcon
                    closable
                    onClose={() => setSuccess("")}
                    style={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        zIndex: 9999,
                        maxWidth: 420,
                        borderRadius: 10
                    }}
                />
            )}

            <style>
                {`
                    .payment-table-row:hover > td {
                        background: #fff7ed !important;
                    }

                    .ant-table-thead > tr > th {
                        background: #111111 !important;
                        color: #ffffff !important;
                        font-weight: 800 !important;
                        font-size: 12px;
                        letter-spacing: 0.4px;
                        white-space: nowrap;
                    }

                    .ant-table-thead > tr > th::before {
                        display: none !important;
                    }

                    .ant-table-tbody > tr > td {
                        padding-top: 15px !important;
                        padding-bottom: 15px !important;
                    }

                    .ant-pagination-item-active {
                        border-color: #ff6b00 !important;
                    }

                    .ant-pagination-item-active a {
                        color: #ff6b00 !important;
                    }

                    .ant-btn-primary {
                        background: #ff6b00;
                        border-color: #ff6b00;
                    }

                    .ant-btn-primary:hover {
                        background: #e85f00 !important;
                        border-color: #e85f00 !important;
                    }

                    .ant-input-affix-wrapper:focus,
                    .ant-input-affix-wrapper-focused,
                    .ant-select-focused .ant-select-selector {
                        border-color: #ff6b00 !important;
                        box-shadow:
                            0 0 0 2px
                            rgba(255,107,0,0.12) !important;
                    }

                    .ant-input-number-focused {
                        border-color: #ff6b00 !important;
                        box-shadow:
                            0 0 0 2px
                            rgba(255,107,0,0.12) !important;
                    }

                    .ant-modal-content {
                        border-radius: 18px !important;
                        overflow: hidden;
                    }

                    .ant-modal-header {
                        border-bottom: 0 !important;
                    }

                    @media (max-width: 768px) {
                        .financial-payment-page {
                            padding: 12px !important;
                        }

                        .ant-card {
                            border-radius: 14px !important;
                        }

                        .ant-statistic-content {
                            font-size: 22px !important;
                        }

                        .ant-table {
                            font-size: 12px;
                        }

                        .ant-modal {
                            max-width: calc(100vw - 20px) !important;
                            margin: 10px auto !important;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default FinancialPaymentRecord;